import { useLang } from "../i18n";
import { useSEO } from "../seo";
import { FadeIn, ParallaxImage } from "../components/Reveal";
import { CTAButton, PageHero, SectionLabel } from "../components/Bits";

const IMG = {
  hero: "https://images.unsplash.com/photo-1519710164239-da123dc03ef4?q=80&w=2000&auto=format&fit=crop",
  detail: "https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?q=80&w=1600&auto=format&fit=crop",
};

const copy = {
  it: {
    seoTitle: "Ristrutturazioni Immobiliari | Nicole Srl",
    seoDesc: "Ristrutturazioni complete e valorizzazione immobiliare anche per conto terzi: dalla strategia al coordinamento delle lavorazioni, fino alla consegna finale.",
    kicker: "Ristrutturazioni",
    lines: ["Trasformiamo immobili.", "Anche per conto", "dei nostri clienti."],
    sub: "Dall'analisi iniziale alla definizione dell'intervento, fino al coordinamento delle lavorazioni e alla consegna finale, seguiamo la trasformazione dell'immobile con lo stesso approccio utilizzato nelle nostre operazioni immobiliari.",
    label: "Ambiti di intervento",
    title: "Cosa possiamo gestire.",
    items: [
      "Ristrutturazioni complete",
      "Riqualificazione di appartamenti",
      "Ristrutturazione di immobili da investimento",
      "Riorganizzazione degli spazi",
      "Finiture",
      "Impianti",
      "Serramenti",
      "Pavimenti e rivestimenti",
      "Illuminazione",
      "Coordinamento delle lavorazioni",
      "Valorizzazione immobiliare",
    ],
    note: "Quando l'intervento richiede attività riservate a professionisti abilitati, operiamo in collaborazione con i tecnici e i professionisti incaricati.",
    ctaTitle: "Hai un immobile da ristrutturare?",
    ctaText: "Raccontaci l'immobile e l'obiettivo: ti rispondiamo con una prima valutazione riservata.",
    cta: "Parlaci del tuo progetto",
  },
  en: {
    seoTitle: "Property Renovations | Nicole Srl",
    seoDesc: "Full renovations and real estate value creation, also on behalf of third-party clients: from strategy to works coordination, through to final delivery.",
    kicker: "Renovations",
    lines: ["We transform properties.", "Also on behalf", "of our clients."],
    sub: "From the initial analysis to the definition of the intervention, through works coordination and final delivery, we manage the property's transformation with the same approach used in our own real estate operations.",
    label: "Scope of work",
    title: "What we can manage.",
    items: [
      "Full renovations",
      "Apartment refurbishment",
      "Renovation of investment properties",
      "Space reorganization",
      "Finishes",
      "Systems",
      "Windows and doors",
      "Flooring and cladding",
      "Lighting",
      "Works coordination",
      "Real estate value creation",
    ],
    note: "Where the intervention requires activities reserved to licensed professionals, we operate in collaboration with the appointed technicians and professionals.",
    ctaTitle: "Have a property to renovate?",
    ctaText: "Tell us about the property and the objective: we will reply with a first confidential assessment.",
    cta: "Tell us about your project",
  },
};

export default function Ristrutturazioni() {
  const { lang } = useLang();
  const t = copy[lang];
  useSEO({ title: t.seoTitle, description: t.seoDesc, path: "/ristrutturazioni", lang });

  return (
    <div data-testid="ristrutturazioni-page">
      <PageHero kicker={t.kicker} lines={t.lines} sub={t.sub} image={IMG.hero} imageAlt={lang === "it" ? "Interno ristrutturato — dettaglio contemporaneo" : "Renovated interior — contemporary detail"} />

      <section className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-36">
        <div className="grid gap-14 md:grid-cols-12">
          <div className="md:col-span-7">
            <FadeIn>
              <SectionLabel>{t.label}</SectionLabel>
              <h2 className="mt-10 font-serif text-4xl font-light leading-[1.08] tracking-tight md:mt-14 md:text-5xl">
                {t.title}
              </h2>
            </FadeIn>
            <div className="mt-12 grid gap-x-14 sm:grid-cols-2">
              {t.items.map((item, i) => (
                <FadeIn key={item} delay={(i % 2) * 0.05}>
                  <div className="flex items-baseline gap-5 border-b border-ink/10 py-5">
                    <span className="h-1.5 w-1.5 shrink-0 rotate-45 bg-bronze" />
                    <span className="text-base leading-snug">{item}</span>
                  </div>
                </FadeIn>
              ))}
            </div>
            <FadeIn delay={0.15}>
              <p className="mt-10 max-w-xl border-l border-bronze pl-6 text-sm leading-relaxed text-stone">{t.note}</p>
            </FadeIn>
          </div>
          <FadeIn className="md:col-span-4 md:col-start-9" delay={0.15}>
            <div className="md:sticky md:top-32">
              <ParallaxImage src={IMG.detail} alt={lang === "it" ? "Dettaglio architettonico di interno ristrutturato" : "Architectural detail of a renovated interior"} className="aspect-[3/4] w-full" />
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="bg-ink text-ivory">
        <div className="mx-auto max-w-7xl px-6 py-24 text-center md:px-10 md:py-36">
          <FadeIn>
            <h2 className="mx-auto max-w-3xl font-serif text-4xl font-light leading-[1.08] tracking-tight md:text-5xl">
              {t.ctaTitle}
            </h2>
            <p className="mx-auto mt-6 max-w-md text-base leading-relaxed text-ivory/70">{t.ctaText}</p>
            <div className="mt-10 flex justify-center">
              <CTAButton to="/contatti" variant="light" testId="ristrutturazioni-cta">
                {t.cta}
              </CTAButton>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
